import { Box, Typography, styled } from "@mui/material";
import { AirportData } from "@/core/types/types";
import { useTranslations as t } from "@/core/hooks/useTranslations/useTranslations";

const StyledBoxWrapper = styled(Box)(({ theme }) => ({
  display: "flex",
  justifyContent: "space-between",
  color: theme.palette.grey.A400,
  flexDirection: "column",
  fontSize: theme.spacing(1.5),
  "& h2": {
    fontWeight: 700,
  },
  "& > div:last-child": {
    textAlign: "end",
  },
}));

const DashedBackground = styled(Box)(() => ({
  position: "relative",
  display: "block",
  width: "100%",
  height: "2px",
  backgroundImage: "linear-gradient(to right, #dddddd 50%, transparent 50%)",
  backgroundSize: "12px 1px",
}));

const StyledArrow = styled(DashedBackground)({
  "&::after": {
    display: "block",
    content: '""',
    position: "absolute",
    top: "-7px",
    right: "-8px",
    borderTop: "8px solid transparent",
    borderLeft: "16px solid #ddd",
    borderBottom: "8px solid transparent",
    borderRight: 0,
  },
});

interface FlightPathBannerProps {
  originAirportData: AirportData;
  destinationAirportData: AirportData;
  hasArrow?: boolean;
  departureStopoverOrder?: string;
  arrivalStopoverOrder?: string;
}

const ArrowPointer = (props: { hasArrow?: boolean }) => {
  return <>{props.hasArrow ? <StyledArrow /> : <DashedBackground />}</>;
};

const FlightPathBanner = ({
  originAirportData,
  destinationAirportData,
  hasArrow = true,
  departureStopoverOrder,
  arrivalStopoverOrder,
}: FlightPathBannerProps) => {
  const stopoverTranslation = "altTransport.itinerary.stopover";
  const departureLabel = hasArrow
    ? departureStopoverOrder
      ? `${departureStopoverOrder}. ${t(stopoverTranslation)}`
      : "flightPicker.departureAirport"
    : "airports.departure.label";
  const arrivalLabel = hasArrow
    ? arrivalStopoverOrder
      ? `${arrivalStopoverOrder}. ${t(stopoverTranslation)}`
      : "flightPicker.arrivalAirport"
    : "airports.arrival.label";

  return (
    <StyledBoxWrapper>
      <Box
        display="flex"
        justifyContent="space-between"
        gap={2}
        alignItems="center"
      >
        <Typography color="secondary" variant="h1">
          {originAirportData?.iata}
        </Typography>
        <ArrowPointer hasArrow={hasArrow} />
        <Typography color="secondary" variant="h1">
          {destinationAirportData?.iata}
        </Typography>
      </Box>

      <Box display="flex" justifyContent="space-between">
        <Box display="flex" flexDirection="column">
          <Typography textAlign="left" variant="body2">
            {t(departureLabel)}
          </Typography>
          <Typography textAlign="left" variant="body2">
            {originAirportData?.name}
          </Typography>
        </Box>
        <Box display="flex" flexDirection="column" alignItems="flex-end">
          <Typography textAlign="right" variant="body2">
            {t(arrivalLabel)}
          </Typography>
          <Typography textAlign="right" variant="body2">
            {destinationAirportData?.name}
          </Typography>
        </Box>
      </Box>
    </StyledBoxWrapper>
  );
};

export default FlightPathBanner;
